import { BodySecondary } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { Component } from 'react';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';

import { modifyParamsRemovePage } from '@/utils/queryParams';

import Facet from './Facet';
import TaxonomyNode from './TaxonomyNode';
import TaxonomyParent from './TaxonomyParent';
import TaxonomySibling from './TaxonomySibling';

export type TaxonomyFacetProps = {
    category: any;
    facetId: string;
    loadPageWithFacetCollapsed?: boolean;
    queryParams: any;
    showLandingPageLinks: boolean;
    taxonomyParentSelected: Function;
    title: string;
};

type State = {
    seeMore: boolean;
};

class TaxonomyFacet extends Component<TaxonomyFacetProps, State> {
    static defaultProps = {
        loadPageWithFacetCollapsed: false,
    };

    state = { seeMore: false };

    shouldComponentUpdate = (nextProps, nextState) =>
        !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);

    componentDidUpdate(prevProps) {
        if (!isEqual(this.props.category, prevProps.category)) {
            this.setSeeMoreToFalse();
        }
    }

    onSeeMore = () => {
        const seeMore = !this.state.seeMore;
        this.setState({ seeMore });
    };

    setSeeMoreToFalse = () => {
        this.setState({ seeMore: false });
    };

    parentSelected = (parentId, categoryId) => {
        this.setSeeMoreToFalse();
        this.props.taxonomyParentSelected(parentId, categoryId, this.props.facetId);
    };

    render() {
        const { category, facetId, loadPageWithFacetCollapsed, showLandingPageLinks, title = '' } = this.props;

        const queryParams = modifyParamsRemovePage(this.props.queryParams);
        const seeMore = this.state.seeMore;

        if (category.facets) {
            const parents = (
                <TaxonomyParent
                    categoryId={category.facets[0].id}
                    parents={category.facets[0].parents}
                    parentSelected={this.parentSelected}
                    queryParams={queryParams}
                    showLandingPageLinks={showLandingPageLinks}
                />
            );
            const siblings = (
                <TaxonomySibling
                    queryParams={queryParams}
                    selectedSiblingChildren={category.facets[0]?.categories}
                    siblings={category.facets[0]?.siblings}
                />
            );
            const children = (
                <TaxonomyNode
                    facetId={facetId}
                    facets={category.facets}
                    onSeeMore={this.onSeeMore}
                    queryParams={queryParams}
                    renderAsParent={category.facets.length > 1}
                    seeMore={seeMore}
                    showLandingPageLinks={showLandingPageLinks}
                />
            );
            const facetClass = title.trim().replace('/', '').replace(/\s+/g, '-').toLowerCase();
            const categoryHasChildren =
                (category.facets[0].categories?.length > 0 || category.facets.length > 1) &&
                !(category.facets[0]?.siblings?.length > 0);

            return (
                <Facet
                    className={`taxonomy-facet ${facetClass}`}
                    closeFacet={loadPageWithFacetCollapsed}
                    testid="categorySection"
                    title={title}
                >
                    <StyledTaxonomyContainer>
                        {parents}
                        {siblings}
                        {categoryHasChildren && children}
                    </StyledTaxonomyContainer>
                </Facet>
            );
        }
        return null;
    }
}

export default TaxonomyFacet;

const StyledTaxonomyContainer = styled(BodySecondary)`
    a {
        display: inline-block;
        font-family: ${({ theme }) => theme.typography.fontPrimary};
        color: ${({ theme }) => theme.colors.grey100};
        line-height: 20px;
        cursor: pointer;
        width: 100%;
        text-decoration: none;

        &:hover {
            color: ${({ theme }) => theme.colors.blue200};
        }
    }

    > a {
        margin: 0 0 10px;
        width: 95.5%;
    }
`;
